import { useCallback, useEffect, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@awesome.me/kit-70c1f0055f/icons/duotone/solid";
import { faTrash } from "@awesome.me/kit-70c1f0055f/icons/classic/regular";

import { reviewWorkflowStepLabel } from "@/components/case-checking/CaseReviewWorkflow";
import { PreviousReviewDiffPanel } from "@/components/case-checking/PreviousReviewDiffPanel";
import { formatReviewRunWhen, reviewRunListLabel } from "@/components/case-checking/guidedReviewLabels";
import {
  relationBadgeLabel,
  restoreButtonLabel,
  restoreConfirmCopy,
} from "@/components/case-checking/previousReviewDiff";
import { ReviewProgressStrip } from "@/components/case-checking/ReviewProgressCompare";
import { Button } from "@/components/ui/button/Button";
import { Badge } from "@/components/ui/badge/Badge";
import { ConfirmDeleteDialog } from "@/components/ui/dialog/ConfirmDeleteDialog";
import { Dialog, DialogActions, DialogBody, DialogTitle } from "@/components/ui/dialog/Dialog";
import {
  deletePsRuleHumanReviewRun,
  getGuidedReviewRestorePreview,
  listPsRuleHumanReviewRuns,
  restoreGuidedReview,
  type GuidedReviewRelation,
  type GuidedReviewRestorePreview,
  type PsRuleHumanReviewRun,
} from "@/services/api/case-checking";

function runStatsLine(run: PsRuleHumanReviewRun): string {
  const parts: string[] = [];
  const reviews = run.reviews?.length ?? 0;
  const disputes = Object.keys(run.checklist_disputes ?? {}).length;
  const remedials = run.remedial_edits?.length ?? 0;
  if (reviews > 0) parts.push(`${reviews} rule edit${reviews === 1 ? "" : "s"}`);
  if (disputes > 0) parts.push(`${disputes} checklist note${disputes === 1 ? "" : "s"}`);
  if (remedials > 0) parts.push(`${remedials} remedial edit${remedials === 1 ? "" : "s"}`);
  if (run.completeness_review) parts.push("completeness decision");
  if (parts.length === 0) return reviewWorkflowStepLabel(run.current_step);
  return parts.join(" · ");
}

export function PreviousReviewsModal({
  open,
  onClose,
  caseId,
  currentReviewRunId,
  onRestored,
}: {
  open: boolean;
  onClose: () => void;
  caseId: string;
  currentReviewRunId?: string | null;
  onRestored?: () => void;
}) {
  const [runs, setRuns] = useState<PsRuleHumanReviewRun[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [selectedRunId, setSelectedRunId] = useState<string | null>(null);
  const [preview, setPreview] = useState<GuidedReviewRestorePreview | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [restoreConfirmOpen, setRestoreConfirmOpen] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [restoreError, setRestoreError] = useState<string | null>(null);
  const [deleteConfirmRunId, setDeleteConfirmRunId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [relationByRunId, setRelationByRunId] = useState<Record<string, GuidedReviewRelation>>({});
  const previewCacheRef = useRef<Map<string, GuidedReviewRestorePreview>>(new Map());

  const rememberPreview = useCallback((runId: string, data: GuidedReviewRestorePreview) => {
    previewCacheRef.current.set(runId, data);
    if (data.differences) {
      setRelationByRunId((prev) => ({ ...prev, [runId]: data.differences.relation }));
    }
  }, []);

  const diff = preview?.differences ?? null;
  const canRestore = Boolean(
    selectedRunId && diff && !diff.is_current_review && diff.relation !== "identical",
  );

  const loadRuns = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const items = await listPsRuleHumanReviewRuns(caseId);
      const sorted = [...items].sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
      setRuns(sorted);
      const defaultId = currentReviewRunId && sorted.some((r) => r.id === currentReviewRunId) ? currentReviewRunId : sorted[0]?.id ?? null;
      setSelectedRunId(defaultId);
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : "Could not load previous reviews");
      setRuns([]);
    } finally {
      setLoading(false);
    }
  }, [caseId, currentReviewRunId]);

  useEffect(() => {
    if (!open) {
      setSelectedRunId(null);
      setPreview(null);
      setPreviewError(null);
      setRestoreError(null);
      setRestoreConfirmOpen(false);
      setDeleteConfirmRunId(null);
      setDeleteError(null);
      setRelationByRunId({});
      previewCacheRef.current.clear();
      return;
    }
    void loadRuns();
  }, [loadRuns, open]);

  useEffect(() => {
    if (!open || !selectedRunId) {
      setPreview(null);
      return;
    }
    const cached = previewCacheRef.current.get(selectedRunId);
    if (cached) {
      setPreview(cached);
      setPreviewLoading(false);
      setPreviewError(null);
      return;
    }
    let cancelled = false;
    setPreviewLoading(true);
    setPreviewError(null);
    void getGuidedReviewRestorePreview(caseId, selectedRunId)
      .then((data) => {
        if (!cancelled) {
          rememberPreview(selectedRunId, data);
          setPreview(data);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setPreview(null);
          setPreviewError(err instanceof Error ? err.message : "Could not load preview");
        }
      })
      .finally(() => {
        if (!cancelled) setPreviewLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [caseId, open, rememberPreview, selectedRunId]);

  const runRestore = useCallback(async () => {
    if (!selectedRunId) return;
    setRestoring(true);
    setRestoreError(null);
    try {
      await restoreGuidedReview(caseId, { prior_review_run_id: selectedRunId });
      setRestoreConfirmOpen(false);
      onRestored?.();
      onClose();
    } catch (err) {
      setRestoreError(err instanceof Error ? err.message : "Could not restore review");
    } finally {
      setRestoring(false);
    }
  }, [caseId, onClose, onRestored, selectedRunId]);

  const runDelete = useCallback(async () => {
    if (!deleteConfirmRunId) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await deletePsRuleHumanReviewRun(caseId, deleteConfirmRunId);
      previewCacheRef.current.delete(deleteConfirmRunId);
      setRelationByRunId((prev) => {
        const next = { ...prev };
        delete next[deleteConfirmRunId];
        return next;
      });
      setDeleteConfirmRunId(null);
      if (selectedRunId === deleteConfirmRunId) {
        setSelectedRunId(null);
        setPreview(null);
      }
      await loadRuns();
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : "Could not delete review");
    } finally {
      setDeleting(false);
    }
  }, [caseId, deleteConfirmRunId, loadRuns, selectedRunId]);

  const deleteTargetRun = deleteConfirmRunId ? runs.find((r) => r.id === deleteConfirmRunId) : null;

  const restoreLabel = diff && !diff.is_current_review ? restoreButtonLabel(diff) : "Restore selected review";
  const confirmDescription = diff ? restoreConfirmCopy(diff) : "";

  return (
    <>
      <Dialog open={open} onClose={onClose} size="5xl" className="sm:max-w-[min(64rem,95vw)]">
        <DialogTitle>Previous reviews</DialogTitle>
        <DialogBody>
          <p className="text-sm text-slate-600 dark:text-slate-300">
            Compare earlier reviews with your current work. Only differences are shown. Restoring replaces your current
            review; the previous version stays in this list.
          </p>
          {loadError ? <p className="mt-3 text-sm text-rose-600" role="alert">{loadError}</p> : null}
          {restoreError ? <p className="mt-3 text-sm text-rose-600" role="alert">{restoreError}</p> : null}
          {deleteError ? <p className="mt-3 text-sm text-rose-600" role="alert">{deleteError}</p> : null}
          <div className="mt-4 grid gap-4 lg:grid-cols-[minmax(13rem,16rem)_1fr] lg:min-h-[min(70vh,28rem)]">
            <div className="flex max-h-[min(70vh,28rem)] flex-col overflow-hidden rounded-lg border border-slate-200/80 dark:border-white/10">
              <p className="border-b border-slate-200/80 px-3 py-2 text-xs font-medium uppercase tracking-wide text-slate-500 dark:border-white/10">
                Timeline
              </p>
              <div className="min-h-0 flex-1 space-y-2 overflow-y-auto p-2">
                {loading ? (
                  <p className="flex items-center gap-2 text-sm text-slate-500">
                    <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
                    Loading…
                  </p>
                ) : null}
                {!loading && runs.length === 0 ? <p className="text-sm text-slate-500">No reviews yet.</p> : null}
                {runs.map((run) => {
                  const selected = run.id === selectedRunId;
                  const isCurrent = run.id === currentReviewRunId;
                  const relation = relationByRunId[run.id];
                  const reviewer = run.created_by_email?.trim() || run.created_by;
                  const canDelete = !isCurrent && run.status !== "active";
                  return (
                    <div key={run.id} className="group relative">
                      <button
                        type="button"
                        className={`w-full rounded-md border px-3 py-2.5 pr-10 text-left text-sm transition ${
                          selected
                            ? "border-indigo-400/60 bg-indigo-500/10 text-indigo-950 dark:text-indigo-100"
                            : "border-transparent bg-slate-800/20 text-slate-300 hover:bg-slate-800/40"
                        }`}
                        onClick={() => setSelectedRunId(run.id)}
                        data-testid={`previous-review-run-${run.id}`}
                      >
                        <span className="flex flex-wrap items-center gap-2">
                          <span className="font-medium">{reviewRunListLabel(run)}</span>
                          {isCurrent ? (
                            <span className="rounded-full bg-indigo-500/20 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-indigo-200">
                              Current
                            </span>
                          ) : null}
                          {relation != null ? (
                            <Badge color="zinc" data-testid={`previous-review-relation-${run.id}`}>
                              {relationBadgeLabel(relation)}
                            </Badge>
                          ) : null}
                          {run.status === "saved" ? (
                            <span className="rounded-full bg-slate-500/20 px-2 py-0.5 text-[10px] font-medium uppercase text-slate-400">
                              Saved
                            </span>
                          ) : null}
                        </span>
                        <ReviewProgressStrip completed={run.completed_steps} className="mt-2" />
                        <span className="mt-1 block text-xs text-slate-500">{formatReviewRunWhen(run.created_at)}</span>
                        <span className="mt-0.5 block text-xs leading-snug text-slate-500">{runStatsLine(run)}</span>
                        {reviewer ? <span className="mt-1 block truncate text-xs text-slate-500">{reviewer}</span> : null}
                      </button>
                      {canDelete ? (
                        <button
                          type="button"
                          className="absolute bottom-2 right-2 rounded-md p-1.5 text-slate-500 opacity-0 transition hover:bg-rose-500/15 hover:text-rose-600 focus:opacity-100 group-hover:opacity-100 dark:hover:text-rose-400"
                          aria-label={`Delete ${reviewRunListLabel(run)}`}
                          data-testid={`previous-review-delete-${run.id}`}
                          onClick={() => setDeleteConfirmRunId(run.id)}
                        >
                          <FontAwesomeIcon icon={faTrash} className="h-3.5 w-3.5" />
                        </button>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            </div>
            <div
              className="min-h-0 max-h-[min(70vh,28rem)] overflow-y-auto rounded-lg border border-slate-200/80 p-4 dark:border-white/10"
              data-testid="previous-review-preview"
            >
              <PreviousReviewDiffPanel preview={preview} loading={previewLoading} error={previewError} />
            </div>
          </div>
        </DialogBody>
        <DialogActions>
          <Button color="slate" type="button" onClick={onClose}>Close</Button>
          <Button
            color="indigo"
            type="button"
            disabled={!canRestore || previewLoading || restoring}
            loading={restoring}
            onClick={() => setRestoreConfirmOpen(true)}
            data-testid="previous-review-restore"
          >
            {restoreLabel}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDeleteDialog
        open={restoreConfirmOpen}
        onClose={() => setRestoreConfirmOpen(false)}
        onConfirm={() => void runRestore()}
        title="Restore this review?"
        description={confirmDescription}
        confirmLabel="Restore"
        cancelLabel="Cancel"
        isDestructive={false}
        confirmButtonColor="indigo"
        isLoading={restoring}
      />

      <ConfirmDeleteDialog
        open={deleteConfirmRunId != null}
        onClose={() => setDeleteConfirmRunId(null)}
        onConfirm={() => void runDelete()}
        title="Delete this review?"
        description={
          deleteTargetRun
            ? `“${reviewRunListLabel(deleteTargetRun)}” will be removed from the timeline. This cannot be undone.`
            : "This review will be removed from the timeline. This cannot be undone."
        }
        confirmLabel="Delete"
        cancelLabel="Cancel"
        isDestructive
        isLoading={deleting}
      />
    </>
  );
}
